import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { mockHotels } from "../data/mockData";
import { Hotel } from "../types";
import Modal from "../components/Modal";
import {
  Star,
  MapPin,
  Wifi,
  Car,
  Utensils,
  Dumbbell,
  Shield,
  ChevronLeft,
  ChevronRight,
  Users,
  Bed,
} from "lucide-react";

const HotelDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedRoom] = useState<string | null>(null);
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState(2);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const foundHotel = mockHotels.find((h) => h.id === id);
    if (foundHotel) {
      setHotel(foundHotel);
    } else {
      navigate("/search");
    }
  }, [id, navigate]);

  if (!hotel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === hotel.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? hotel.images.length - 1 : prev - 1
    );
  };

  const handleBookNow = (roomId: string) => {
    if (!checkIn || !checkOut) {
      setShowModal(true);
      return;
    }
    navigate(
      `/booking/${
        hotel.id
      }/${roomId}?checkIn=${checkIn.toISOString()}&checkOut=${checkOut.toISOString()}&guests=${guests}`
    );
  };

  const renderStars = (stars: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < stars ? "text-yellow-400 fill-current" : "text-gray-300"
        }`}
      />
    ));
  };

  const getAmenityIcon = (amenity: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      "Free WiFi": <Wifi className="w-5 h-5" />,
      Pool: <div className="w-5 h-5 bg-blue-500 rounded-full"></div>,
      Restaurant: <Utensils className="w-5 h-5" />,
      "Fitness Center": <Dumbbell className="w-5 h-5" />,
      "Airport Shuttle": <Car className="w-5 h-5" />,
      Spa: <Shield className="w-5 h-5" />,
    };
    return (
      iconMap[amenity] || (
        <div className="w-5 h-5 bg-gray-400 rounded-full"></div>
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate("/search")}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to search results</span>
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Hotel Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {hotel.name}
              </h1>
              <div className="flex items-center text-gray-600 mb-4">
                <MapPin className="w-5 h-5 mr-2" />
                <span>{hotel.location}</span>
              </div>
              <div className="flex items-center space-x-1 mb-4">
                {renderStars(hotel.stars)}
                <span className="ml-2 text-lg font-semibold">
                  {hotel.rating}
                </span>
                <span className="text-gray-600">
                  ({hotel.reviewCount} reviews)
                </span>
              </div>
              <p className="text-gray-700 mb-6">{hotel.description}</p>
            </div>
            <div className="lg:ml-8">
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-900">
                  ${hotel.price}
                  <span className="text-lg font-normal text-gray-600">
                    /night
                  </span>
                </div>
                <div className="text-sm text-gray-600">per room</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="relative h-96">
                <img
                  src={hotel.images[currentImageIndex]}
                  alt={hotel.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {hotel.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full ${
                        index === currentImageIndex ? "bg-white" : "bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Amenities
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hotel.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    {getAmenityIcon(amenity)}
                    <span className="text-gray-700">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Rooms */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Available Rooms
              </h3>
              <div className="space-y-4">
                {hotel.rooms.map((room) => (
                  <div
                    key={room.id}
                    className={`border rounded-lg p-4 ${
                      selectedRoom === room.id
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {room.type}
                        </h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 my-2">
                          <div className="flex items-center space-x-1">
                            <Bed className="w-4 h-4" />
                            <span>{room.bedType}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>Up to {room.maxOccupancy} guests</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 my-2">
                          {room.amenities.map((amenity, index) => (
                            <span
                              key={index}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                            >
                              {amenity}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-gray-600">
                          {room.cancellationPolicy}
                        </p>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-6 text-right">
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          ${room.price}
                          <span className="text-sm font-normal text-gray-600">
                            /night
                          </span>
                        </div>
                        <button
                          onClick={() => handleBookNow(room.id)}
                          className="btn btn-primary w-full md:w-auto"
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Book Your Stay
              </h3>

              {/* Date Selection */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-in Date
                  </label>
                  <input
                    type="date"
                    value={checkIn ? checkIn.toISOString().split("T")[0] : ""}
                    onChange={(e) =>
                      setCheckIn(
                        e.target.value ? new Date(e.target.value) : null
                      )
                    }
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-out Date
                  </label>
                  <input
                    type="date"
                    value={checkOut ? checkOut.toISOString().split("T")[0] : ""}
                    onChange={(e) =>
                      setCheckOut(
                        e.target.value ? new Date(e.target.value) : null
                      )
                    }
                    min={
                      checkIn
                        ? checkIn.toISOString().split("T")[0]
                        : new Date().toISOString().split("T")[0]
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Guests
                  </label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value={1}>1 Guest</option>
                    <option value={2}>2 Guests</option>
                    <option value={3}>3 Guests</option>
                    <option value={4}>4 Guests</option>
                    <option value={5}>5+ Guests</option>
                  </select>
                </div>
              </div>

              {/* Price Summary */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Room rate (1 night)</span>
                  <span>${hotel.price}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Taxes & fees</span>
                  <span>${Math.round(hotel.price * 0.15)}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-lg font-semibold text-gray-900">
                    <span>Total</span>
                    <span>${hotel.price + Math.round(hotel.price * 0.15)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for missing dates */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Missing Information"
      >
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Please select check-in and check-out dates before booking.
          </p>
          <button
            onClick={() => setShowModal(false)}
            className="btn btn-primary"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default HotelDetailPage;
